import moment from 'moment';
import Vue from 'vue';

export class DateHelper {
    
    constructor(){
        // empty
    }
    
    public formatDateWeekdayName(dateString): string {
        const locale = Vue.i18n.locale();
        return moment(dateString).toDate().toLocaleDateString(
            locale, 
            {
                weekday: "long"
            }
        );
    }

    public formatDateWithShortWeekDay(dateString): string {
        const locale = Vue.i18n.locale();
        return moment(dateString).toDate().toLocaleDateString(
            locale, 
            {
                weekday: "short",
                month: "numeric",
                day: "numeric",
                year: "numeric"
            }
        );
    }

    public formatDateTime(date: Date | string | null | undefined): string {
        if (!date) {
            return '';
        }
        
        const locale = Vue.i18n.locale();
        return moment(date).toDate().toLocaleDateString(
            locale, 
            {
                month: "numeric",
                day: "numeric",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            }
        );
    }

    public formatDateTimeWithShortWeekDay(date: Date): string {
        const locale = Vue.i18n.locale();
        return moment(date).toDate().toLocaleDateString(
            locale, 
            {
                weekday: "short",
                month: "numeric",
                day: "numeric",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            }
        );      
    }

    public formatDateWithLongWeekDay(dateString): string {
        const locale = Vue.i18n.locale();
        return moment(dateString).toDate().toLocaleDateString(
            locale, 
            {
                weekday: "long",
                month: "numeric",
                day: "numeric",
                year: "numeric"
            }
        );
    }

    public formatDateShort(dateString): string {
        if (!dateString) {
            return '';
        }

        const locale = Vue.i18n.locale();
        return moment(dateString).toDate().toLocaleDateString(
            locale, 
            {
                month: "numeric",
                day: "numeric",
                year: "numeric"
            }
        );
    }

    public datePickerTitleFormat(val, locale): string {
        const currentLocale = locale ? locale : Vue.i18n.locale();
        return moment(val).toDate().toLocaleDateString(
            currentLocale, 
            {
                weekday: "short",
                month: "long",
                day: "numeric"
            }
        );
    }

    public dateYMD(dateValue: Date | string): string {
        if (!dateValue) {
            return '';
        }
        const momentDate = moment(dateValue);
        const momentResult = momentDate.format('YYYY-MM-DD');
        return momentResult;
    }

    public timeHHMM(dateValue: Date | string): string {
        return moment(dateValue).format('HH:mm');
    }
    
    public formatDateTimeWithShortWeekDayAndTime(val): string {
        const locale = Vue.i18n.locale();
        const dateStr =  moment(val).toDate().toLocaleDateString(
            locale, 
            {
                weekday: "short",
                month: "numeric",
                day: "numeric",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            }
        );
        return dateStr;
    }

    public getDateFromDateString(dateString: string): Date {
        const momentDate = moment(dateString);
        return momentDate.toDate();
    }

    public getDateStringFromDate(date: Date): string {
        const momentDate = moment(date);
        return momentDate.format('YYYY-MM-DD');
    }

    public isInValidityPeriod(validFrom: NullableDateValue, validUntil: NullableDateValue) {
        const now = moment();

        const validFromMoment = this.isDefinedDate(validFrom) ? moment(validFrom) : null;
        const validUntilMoment = this.isDefinedDate(validUntil) ? moment(validUntil) : null;

        if (!validFromMoment && !validUntilMoment) {
            // no period defined, always valid
            return true;
        }

        if (validFromMoment && validUntilMoment) {
            // both validFrom and validUntil defined, check if now is between them
            return now.isBetween(validFromMoment, validUntilMoment, undefined, '[]');
        }

        if (validFromMoment && !validUntilMoment) {
            // only validFrom defined, check if now is after or same as validFrom
            return now.isSameOrAfter(validFromMoment);
        }

        if (!validFromMoment && validUntilMoment) {
            // only validUntil defined, check if now is before or same as validUntil
            return now.isSameOrBefore(validUntilMoment);
        }

        // We should not reach this point, but return false just in case
        return false;
    }

    private isDefinedDate(date: NullableDateValue): date is DateValue {
        return date !== null && date !== undefined;
    }
}

export type NullableDateValue = moment.Moment | Date | string | null | undefined;
export type DateValue = moment.Moment | Date | string;

export default DateHelper;
